import React from 'react';
import './Message.css';

// Simple phone-number linkification for web UI.
// Converts US-style numbers like:
//   (678) 555-1212
//   678-555-1212
//   678 555 1212
//   +1 678-555-1212
// into clickable <a href="tel:..."> links.
const PHONE_RE = /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}/g;

function normalizeTelHref(phoneText) {
  const digits = (phoneText || '').replace(/\D/g, '');
  if (!digits) return '';
  // If it's 11 digits and starts with leading country code, keep it.
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  // Otherwise assume US 10-digit number.
  if (digits.length === 10) return `+1${digits}`;
  // Fallback: just prefix '+' if it looks like a country code already.
  return digits.startsWith('1') ? `+${digits}` : `+${digits}`;
}

function linkifyPhones(text) {
  if (!text) return text;

  const parts = [];
  let lastIndex = 0;
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = PHONE_RE.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }
    const phoneText = match[0];
    const href = normalizeTelHref(phoneText);
    if (href) {
      parts.push(
        <a
          key={`${start}-${end}`}
          className="phone-link"
          href={`tel:${href}`}
        >
          {phoneText}
        </a>
      );
    } else {
      parts.push(phoneText);
    }
    lastIndex = end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
}

function Message({ message }) {
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    // Show both date and time to avoid many entries looking identical within the same minute
    return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' });
  };

  return (
    <div className={`message message-${message.type}`}>
      <div className="message-content">
        <div className="message-text">{linkifyPhones(message.text)}</div>
        <div className="message-meta">
          {message.channel && (
            <span 
              className={`message-channel-icon message-channel-${message.channel}`} 
              title={message.channel.toUpperCase()}
            >
              {message.channel === 'sms' ? (
                // Chat bubble icon for SMS
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              ) : message.channel === 'voice' ? (
                // Phone icon for voice
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2c-3.18-.35-6.18-1.73-8.63-3.94a19.5 19.5 0 0 1-6-6C3.9 9.53 2.52 6.53 2.18 3.35A2 2 0 0 1 4.11 1.17h3a2 2 0 0 1 2 1.72c.12.88.3 1.75.57 2.58a2 2 0 0 1-.45 2.11L8 9.85a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45c.83.27 1.7.45 2.58.57A2 2 0 0 1 22 16.92z"></path>
                </svg>
              ) : (
                // Globe icon for web (default)
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
              )}
            </span>
          )}
          {message.timestamp && (
            <span className="message-time">{formatTime(message.timestamp)}</span>
          )}
        </div>
        {message.disclaimer && (
          <div className="message-disclaimer">
            <strong>Note:</strong> {message.disclaimer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Message;
